import { RestRequest, RestContext, ResponseFunction } from 'msw'
import { requireAuthenticatedUser } from '../../decorators'
import { fakeToken } from '../users/authenticate'
import { validationFailureBody } from '../../modules/validationFailure'
import {
  consumeCeremony,
  issueChallenge,
  MOCK_USER_EMAIL,
  MOCK_USER_HANDLE,
  readAuthMethods,
  readRpId,
  readPasskeys,
  toPublicPasskey,
  writePasskeys,
  type IMockPasskey,
} from './mockPasskeyState'
import { PASSKEY_PROBLEM_TYPES } from '~/types/passkey/models'

// Mocks for cynque-api-server#1183's passkey routes. Status codes, problem
// `type`s and `detail`s are the api's (errors.py), so the webapp's error
// handling is exercised against what it will really receive.

const TIMEOUT_MS = 300_000
const DEFAULT_LABEL = 'Passkey'
const MAX_LABEL_LENGTH = 100

function problem(
  res: ResponseFunction,
  ctx: RestContext,
  status: number,
  type: string,
  title: string,
  detail: string
) {
  return res(ctx.status(status), ctx.json({ status, type, title, detail }))
}

function refuseSignIn(res: ResponseFunction, ctx: RestContext) {
  return problem(
    res,
    ctx,
    403,
    PASSKEY_PROBLEM_TYPES.invalidCredentials,
    'User authentication failure',
    'Credentials are invalid.'
  )
}

function notFound(res: ResponseFunction, ctx: RestContext) {
  return problem(
    res,
    ctx,
    404,
    PASSKEY_PROBLEM_TYPES.notFound,
    'Passkey not found',
    'No passkey with that id.'
  )
}

async function readBody(req: RestRequest): Promise<any> {
  try {
    return await req.json()
  } catch {
    return undefined
  }
}

// -- sign-in (public) ---------------------------------------------------------------

async function getAuthenticationMethods(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  return res(ctx.status(200), ctx.json({ data: readAuthMethods() }))
}

async function postAuthenticationOptions(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  return res(
    ctx.status(200),
    ctx.json({
      data: {
        challenge: issueChallenge('authentication'),
        timeout: TIMEOUT_MS,
        // The real api takes this from WEBAPP_ORIGIN; the page's own host is
        // what a local WEBAPP_ORIGIN of http://localhost:3000 amounts to.
        rpId: readRpId(),
        allowCredentials: [],
        userVerification: 'required',
      },
    })
  )
}

async function postAuthentication(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const body = await readBody(req)
  if (!consumeCeremony(body?.response?.clientDataJSON, 'authentication')) {
    return refuseSignIn(res, ctx)
  }
  const passkeys = readPasskeys()
  const passkey = passkeys.find((p) => p.credentialId === body?.rawId)
  if (!passkey || body?.response?.userHandle !== MOCK_USER_HANDLE) {
    return refuseSignIn(res, ctx)
  }
  passkey.lastUsedAt = new Date().toISOString()
  writePasskeys(passkeys)
  return res(ctx.status(200), ctx.json({ data: { authToken: fakeToken() } }))
}

// -- the signed-in user's passkeys --------------------------------------------------

async function getPasskeys(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  return res(
    ctx.status(200),
    ctx.json({ data: readPasskeys().map(toPublicPasskey) })
  )
}

async function postRegistrationOptions(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  return res(
    ctx.status(200),
    ctx.json({
      data: {
        rp: { name: 'Cynque', id: readRpId() },
        user: {
          id: MOCK_USER_HANDLE,
          name: MOCK_USER_EMAIL,
          displayName: MOCK_USER_EMAIL,
        },
        challenge: issueChallenge('registration'),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        timeout: TIMEOUT_MS,
        excludeCredentials: readPasskeys().map((p) => ({
          type: 'public-key',
          id: p.credentialId,
          transports: p.transports,
        })),
        authenticatorSelection: {
          residentKey: 'required',
          requireResidentKey: true,
          userVerification: 'required',
        },
        attestation: 'none',
      },
    })
  )
}

async function postRegistration(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const body = await readBody(req)
  const credential = body?.credential
  if (
    !consumeCeremony(credential?.response?.clientDataJSON, 'registration') ||
    typeof credential?.id !== 'string'
  ) {
    return problem(
      res,
      ctx,
      400,
      PASSKEY_PROBLEM_TYPES.registrationInvalid,
      'Passkey registration failed',
      'The passkey could not be registered. Start the registration again.'
    )
  }
  const passkeys = readPasskeys()
  if (passkeys.some((p) => p.credentialId === credential.id)) {
    return problem(
      res,
      ctx,
      409,
      PASSKEY_PROBLEM_TYPES.alreadyRegistered,
      'Passkey already registered',
      'This passkey is already registered.'
    )
  }
  const label =
    typeof body?.label === 'string' && body.label.trim()
      ? body.label.trim()
      : DEFAULT_LABEL
  const passkey: IMockPasskey = {
    id: crypto.randomUUID(),
    credentialId: credential.id,
    label,
    createdAt: new Date().toISOString(),
    lastUsedAt: null,
    backedUp: false,
    transports: Array.isArray(credential.response?.transports)
      ? credential.response.transports
      : [],
  }
  writePasskeys([...passkeys, passkey])
  return res(ctx.status(201), ctx.json({ data: toPublicPasskey(passkey) }))
}

async function patchPasskey(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const body = await readBody(req)
  const label = typeof body?.label === 'string' ? body.label.trim() : ''
  if (!label || label.length > MAX_LABEL_LENGTH) {
    // The api's pydantic constraint (strip, 1–100 characters) as Litestar
    // reports it.
    return res(
      ctx.status(400),
      ctx.json({
        ...validationFailureBody({
          path: '/api/users/current/passkeys',
          extra: [
            { message: 'String should have 1 to 100 characters', key: 'label' },
          ],
        }),
        detail: `Validation failed for PATCH /api/users/current/passkeys/${req.params.id}`,
      })
    )
  }
  const passkeys = readPasskeys()
  const passkey = passkeys.find((p) => p.id === req.params.id)
  if (!passkey) {
    return notFound(res, ctx)
  }
  passkey.label = label
  writePasskeys(passkeys)
  return res(ctx.status(200), ctx.json({ data: toPublicPasskey(passkey) }))
}

async function deletePasskey(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const passkeys = readPasskeys()
  if (!passkeys.some((p) => p.id === req.params.id)) {
    return notFound(res, ctx)
  }
  // The api's guard: the last passkey of a user with no working password.
  // The mock models only the deployment-wide switch, not a per-user one.
  if (passkeys.length === 1 && !readAuthMethods().includes('password')) {
    return problem(
      res,
      ctx,
      409,
      PASSKEY_PROBLEM_TYPES.lastSignInMethod,
      'Last sign-in method',
      'This is your only way to sign in. Add another passkey before removing this one.'
    )
  }
  writePasskeys(passkeys.filter((p) => p.id !== req.params.id))
  return res(ctx.status(204))
}

const publicHandlers = [
  {
    method: 'get',
    path: '/api/users/authentication/methods',
    handler: getAuthenticationMethods,
  },
  {
    method: 'post',
    path: '/api/users/passkeys/authentication/options',
    handler: postAuthenticationOptions,
  },
  {
    method: 'post',
    path: '/api/users/passkeys/authentication',
    handler: postAuthentication,
  },
]

const authenticatedHandlers = [
  {
    method: 'get',
    path: '/api/users/current/passkeys',
    handler: getPasskeys,
  },
  {
    method: 'post',
    path: '/api/users/current/passkeys/registration/options',
    handler: postRegistrationOptions,
  },
  {
    method: 'post',
    path: '/api/users/current/passkeys/registration',
    handler: postRegistration,
  },
  {
    method: 'patch',
    path: '/api/users/current/passkeys/:id',
    handler: patchPasskey,
  },
  {
    method: 'delete',
    path: '/api/users/current/passkeys/:id',
    handler: deletePasskey,
  },
].map((h) => ({ ...h, handler: requireAuthenticatedUser(h.handler) }))

export const handlers = [...publicHandlers, ...authenticatedHandlers]
